import React, { useState } from "react";
import { formatDistance } from "date-fns";
import { ProgressSpinner } from "primereact/progressspinner";
import { DeviceLink, useRemoveDeviceLink } from "hooks/useDealers";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "primereact/confirmdialog";
import styled from "styled-components";
import { theme } from "styles/theme";
import AddDeviceLinkDialog from "./AddDeviceLinkDialog";
import AdminTemporaryAccessDialog from "components/dealers/dealerDetails/AdminTemporaryAccessDialog";
import { Button } from "primereact/button";
import { Link, useNavigate } from "react-router-dom";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";
import { notify } from "utils/notify";
import { useAdminCancelTemporaryDealerAccess } from "hooks/useTemporaryDealerAccess";
import {
  AdminTemporaryAccessMode,
  DealerTemporaryAccess,
} from "types/temporaryAccess.types";
import { SectionTitle } from "styles/components/dealerDetails.styles";

const LinksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
`;

const AddLinkButton = styled(Button)`
  font-weight: 600;
`;

const DeviceLinkCard = styled.div<{ $temporary?: boolean }>`
  background-color: ${theme.colors.bgLight};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  border: 1px solid ${theme.colors.border};
  border-left: 4px ${({ $temporary }) => ($temporary ? "dashed" : "solid")}
    var(--davey-section-accent);
  padding: 1rem;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  color: ${theme.colors.textPrimary};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
`;

const TemporaryAccessSection = styled.section`
  margin-top: 2rem;
`;

/** Same grid as the permanent links, rendered as a real list. */
const TemporaryAccessList = styled(LinksContainer)`
  margin-bottom: 0;
  padding: 0;
  list-style: none;
`;

/**
 * Stretched link: the ::after overlay makes the whole card open the device,
 * while the X stays a separate control (a button can't sit inside a link).
 */
const CardLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: ${theme.borderRadius.medium};
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: 2px solid var(--davey-section-accent);
    outline-offset: 2px;
  }
`;

const SerialNumber = styled.h3`
  color: ${theme.colors.textPrimary};
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  padding-right: 30px;
`;

const DeviceInfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0;
`;

const DeviceInfoItem = styled.li`
  display: flex;
  margin-bottom: 0.5rem;
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.textSecondary};

  span:first-child {
    font-weight: ${theme.fontWeights.medium};
    margin-right: 0.5rem;
    min-width: 80px;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.small};
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.medium};
  text-transform: uppercase;
  background-color: ${(props) => {
    switch (props.status) {
      case "accepted":
      case "active":
        return theme.colors.statusActive || "#d4edda";
      case "pending":
        return theme.colors.warning + "20" || "#fff3cd";
      case "rejected":
        return theme.colors.statusInactive || "#f8d7da";
      default:
        return theme.colors.bgLight || "#e9ecef";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "accepted":
      case "active":
        return theme.colors.statusActiveText || "#155724";
      case "pending":
        return "#856404";
      case "rejected":
        return theme.colors.statusInactiveText || "#721c24";
      default:
        return theme.colors.textPrimary || "#495057";
    }
  }};
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: ${theme.colors.error};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 1rem;
  /* Above the temporary cards' stretched-link overlay. */
  z-index: 1;

  &:hover {
    background-color: ${theme.colors.error + "20"};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.textSecondary};
  background-color: ${theme.colors.bgLight};
  border-radius: ${theme.borderRadius.medium};
  margin-top: 1rem;
`;

/** Relative time ("in 3 days") with the exact date on hover. */
const RelativeTime: React.FC<{ value?: string | null }> = ({ value }) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return <>Unknown</>;
  return (
    <time
      dateTime={date.toISOString()}
      title={date.toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })}
    >
      {formatDistance(date, new Date(), { addSuffix: true })}
    </time>
  );
};

interface DeviceLinksListProps {
  deviceLinks: DeviceLink[];
  /** Pending and active temporary access, shown below the permanent links. */
  temporaryAccess?: DealerTemporaryAccess[];
  dealerId: string | number;
  isLoading: boolean;
  dealerName?: string;
}

const DeviceLinksList: React.FC<DeviceLinksListProps> = ({
  deviceLinks,
  temporaryAccess = [],
  dealerId,
  isLoading,
  dealerName = "Dealer",
}) => {
  const [selectedDeviceLink, setSelectedDeviceLink] =
    useState<DeviceLink | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [addLinkDialogVisible, setAddLinkDialogVisible] = useState(false);
  // Mode is kept after closing so the dialog copy doesn't flip mid-animation.
  const [temporaryAccessDialog, setTemporaryAccessDialog] = useState<{
    visible: boolean;
    mode: AdminTemporaryAccessMode;
  }>({ visible: false, mode: "grant" });
  // Kept after closing, like the unlink dialog, so its copy doesn't blank
  // out during the hide animation.
  const [temporaryAccessToCancel, setTemporaryAccessToCancel] =
    useState<DealerTemporaryAccess | null>(null);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: removeDeviceLink } = useRemoveDeviceLink();
  const {
    mutate: cancelTemporaryAccess,
    isPending: isCancellingTemporaryAccess,
    variables: cancelVariables,
  } = useAdminCancelTemporaryDealerAccess();
  const cancellingRequestId = isCancellingTemporaryAccess
    ? cancelVariables?.requestId
    : undefined;

  const formatDate = (dateString: string) => {
    try {
      return formatDistance(new Date(dateString), new Date(), {
        addSuffix: true,
      });
    } catch (error) {
      return "Unknown";
    }
  };

  const handleRemoveClick = (
    deviceLink: DeviceLink,
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedDeviceLink(deviceLink);
    setConfirmDialogVisible(true);
  };

  const confirmRemove = () => {
    if (!selectedDeviceLink) return;

    removeDeviceLink(
      {
        linkId: selectedDeviceLink.linkId,
      },
      {
        onSuccess: () => {
          notify.success(`Device ${selectedDeviceLink.serialNumber} has been unlinked from this dealer.`);
          queryClient.invalidateQueries({
            queryKey: ["dealerInfo", dealerId.toString()],
          });
          setConfirmDialogVisible(false);
          setSelectedDeviceLink(null);
        },
        onError: (error: any) => {
          notify.error(error?.message || "Failed to unlink device. Please try again.");
          setConfirmDialogVisible(false);
        },
      }
    );
  };

  const handleAddDeviceLink = () => {
    setAddLinkDialogVisible(true);
  };

  const openTemporaryAccessDialog = (mode: AdminTemporaryAccessMode) => {
    setTemporaryAccessDialog({ visible: true, mode });
  };

  const closeTemporaryAccessDialog = () => {
    setTemporaryAccessDialog((current) => ({ ...current, visible: false }));
  };

  const handleCancelTemporaryAccessClick = (access: DealerTemporaryAccess) => {
    setTemporaryAccessToCancel(access);
    setCancelDialogVisible(true);
  };

  const confirmCancelTemporaryAccess = () => {
    const access = temporaryAccessToCancel;
    if (!access) return;
    setCancelDialogVisible(false);

    cancelTemporaryAccess(
      { dealerId: Number(dealerId), requestId: access.id },
      {
        // Word the toast from the API result: the request may have been
        // approved after the page loaded, in which case access was revoked.
        onSuccess: (result) => {
          notify.success(
            result.status === "revoked"
              ? `${dealerName}'s temporary access to ${access.serialNumber} has ended.`
              : `Temporary access request for ${access.serialNumber} was cancelled.`,
          );
        },
        onError: (error: any) => {
          notify.error(
            error?.response?.data?.message ||
              "Failed to cancel temporary access. Please try again.",
          );
        },
      },
    );
  };

  const endingActiveAccess = temporaryAccessToCancel?.status === "active";
  const cancelDialogCopy = endingActiveAccess
    ? {
        header: "End Temporary Access",
        message: `End ${dealerName}'s temporary access to ${temporaryAccessToCancel?.serialNumber} now? The dealer loses access to this device immediately.`,
        acceptLabel: "End Access",
        rejectLabel: "Keep Access",
      }
    : {
        header: "Cancel Temporary Access Request",
        message: `Cancel the pending temporary access request for ${temporaryAccessToCancel?.serialNumber}? The device owner will no longer be able to approve it.`,
        acceptLabel: "Cancel Request",
        rejectLabel: "Keep Request",
      };

  const handleDeviceClick = (link: DeviceLink) => {
    navigate(
      buildDeviceDetailsPath({
        serialNumber: link.serialNumber,
        linkId: link.linkId,
        isAdminView: true,
      }),
    );
  };

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }

  return (
    <>
<HeaderContainer>
        {deviceLinks.length > 0 && (
          <div>
            <span>{deviceLinks.length} device(s) linked</span>
          </div>
        )}
        <HeaderActions>
          <Button
            label="Grant Temporary Access"
            icon="pi pi-clock"
            onClick={() => openTemporaryAccessDialog("grant")}
            className="p-button-outlined"
            tooltip="Give this dealer 72 hours of access now, without emailing the customer"
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            label="Request Temporary Access"
            icon="pi pi-envelope"
            onClick={() => openTemporaryAccessDialog("request")}
            className="p-button-outlined"
            tooltip="Email the device owner to approve 72 hours of access for this dealer"
            tooltipOptions={{ position: "bottom" }}
          />
          <AddLinkButton
            label="+ Add Device Link"
            icon="pi pi-link"
            onClick={handleAddDeviceLink}
            className="p-button-primary"
          />
        </HeaderActions>
      </HeaderContainer>

      {deviceLinks.length === 0 ? (
        <EmptyMessage>
          <p>
            This dealer has no linked devices. Click "Add Device Link" to link a
            device.
          </p>
        </EmptyMessage>
      ) : (
        <LinksContainer>
          {deviceLinks.map((link) => (
            <DeviceLinkCard
              key={link.linkId}
              onClick={() => handleDeviceClick(link)}
            >
              <SerialNumber>{link.serialNumber}</SerialNumber>
              <StatusBadge status={link.status}>{link.status}</StatusBadge>
              <DeviceInfoList>
                <DeviceInfoItem>
                  <span>Device ID:</span>
                  <span>{link.deviceId}</span>
                </DeviceInfoItem>
                <DeviceInfoItem>
                  <span>Linked:</span>
                  <span>{formatDate(link.createdAt)}</span>
                </DeviceInfoItem>
                <DeviceInfoItem>
                  <span>Updated:</span>
                  <span>{formatDate(link.updatedAt)}</span>
                </DeviceInfoItem>
              </DeviceInfoList>
              <RemoveButton
                onClick={(e) => handleRemoveClick(link, e)}
                title="Unlink device"
                aria-label="Unlink device"
              >
                <i className="pi pi-times" />
              </RemoveButton>
            </DeviceLinkCard>
          ))}
        </LinksContainer>
      )}

      {temporaryAccess.length > 0 && (
        <TemporaryAccessSection aria-labelledby="dealer-temporary-access-title">
          <SectionTitle id="dealer-temporary-access-title">
            Temporary Access ({temporaryAccess.length})
          </SectionTitle>
          <p>
            Active access ends automatically after 72 hours. Pending requests
            are waiting for the device owner to approve them.
          </p>
          <TemporaryAccessList as="ul" role="list">
            {temporaryAccess.map((access) => {
              const isActive = access.status === "active";
              const isEnding = cancellingRequestId === access.id;
              return (
                <DeviceLinkCard key={access.id} as="li" $temporary>
                  <SerialNumber>
                    <CardLink
                      to={buildDeviceDetailsPath({
                        serialNumber: access.serialNumber,
                        isAdminView: true,
                      })}
                    >
                      {access.serialNumber}
                    </CardLink>
                  </SerialNumber>
                  <StatusBadge status={access.status}>
                    {isActive ? "Active" : "Pending"}
                  </StatusBadge>
                  <DeviceInfoList>
                    <DeviceInfoItem>
                      <span>Device ID:</span>
                      <span>{access.deviceId ?? "—"}</span>
                    </DeviceInfoItem>
                    {isActive ? (
                      <>
                        <DeviceInfoItem>
                          <span>Started:</span>
                          <span>
                            <RelativeTime
                              value={access.approvedAt ?? access.createdAt}
                            />
                          </span>
                        </DeviceInfoItem>
                        <DeviceInfoItem>
                          <span>Ends:</span>
                          <span>
                            <RelativeTime value={access.accessExpiresAt} />
                          </span>
                        </DeviceInfoItem>
                      </>
                    ) : (
                      <>
                        <DeviceInfoItem>
                          <span>Requested:</span>
                          <span>
                            <RelativeTime value={access.createdAt} />
                          </span>
                        </DeviceInfoItem>
                        <DeviceInfoItem>
                          <span>Request expires:</span>
                          <span>
                            <RelativeTime value={access.requestExpiresAt} />
                          </span>
                        </DeviceInfoItem>
                      </>
                    )}
                  </DeviceInfoList>
                  <RemoveButton
                    type="button"
                    onClick={() => handleCancelTemporaryAccessClick(access)}
                    disabled={isEnding}
                    title={
                      isActive
                        ? "End temporary access"
                        : "Cancel temporary access request"
                    }
                    aria-label={
                      isActive
                        ? `End temporary access to ${access.serialNumber}`
                        : `Cancel temporary access request for ${access.serialNumber}`
                    }
                  >
                    <i
                      className={isEnding ? "pi pi-spin pi-spinner" : "pi pi-times"}
                      aria-hidden="true"
                    />
                  </RemoveButton>
                </DeviceLinkCard>
              );
            })}
          </TemporaryAccessList>
        </TemporaryAccessSection>
      )}

      <ConfirmDialog
        visible={confirmDialogVisible}
        onHide={() => setConfirmDialogVisible(false)}
        message={`Are you sure you want to unlink the device ${selectedDeviceLink?.serialNumber}? This action cannot be undone.`}
        header="Confirm Device Unlink"
        icon="pi pi-exclamation-triangle"
        acceptClassName="p-button-danger"
        accept={confirmRemove}
        reject={() => setConfirmDialogVisible(false)}
        acceptLabel="Unlink"
        rejectLabel="Cancel"
      />

      <ConfirmDialog
        visible={cancelDialogVisible}
        onHide={() => setCancelDialogVisible(false)}
        message={cancelDialogCopy.message}
        header={cancelDialogCopy.header}
        icon="pi pi-exclamation-triangle"
        acceptClassName="p-button-danger"
        accept={confirmCancelTemporaryAccess}
        reject={() => setCancelDialogVisible(false)}
        acceptLabel={cancelDialogCopy.acceptLabel}
        rejectLabel={cancelDialogCopy.rejectLabel}
      />

      <AddDeviceLinkDialog
        visible={addLinkDialogVisible}
        onHide={() => setAddLinkDialogVisible(false)}
        dealerId={Number(dealerId)}
        dealerName={dealerName}
      />

      <AdminTemporaryAccessDialog
        visible={temporaryAccessDialog.visible}
        mode={temporaryAccessDialog.mode}
        onHide={closeTemporaryAccessDialog}
        dealerId={Number(dealerId)}
        dealerName={dealerName}
      />
    </>
  );
};

export default DeviceLinksList;
